import { IDealsDashboardFilters } from '@/bundles/Deals/types';
import { IDealsDashboardFiltersDto } from '@/bundles/Deals/dto';
import { DealsMeasureEnum } from '@/bundles/Deals/types/enums/DealsMeasureEnum';
import { DealPaymentStatusEnum } from '@/bundles/Deals/types/enums/DealPaymentStatusEnum';
import {
  IQueryRecord,
  enumToQuery,
  queryToEnum,
  stringArraysEqual,
  toArray,
  toNumberArray,
} from '@/bundles/DashboardAnalytics/helpers/dashboardFiltersQuery';
import {
  BASE_FILTERS_QUERY_KEYS,
  baseFiltersEqual,
  baseFiltersToQuery,
  baseQueryHasFilters,
  baseQueryToFilters,
  dealsPipelineFiltersToDto,
} from '@/bundles/DashboardAnalytics/helpers/dealsPipelineFiltersQuery';

export { viewToQuery, queryToView } from '@/bundles/DashboardAnalytics/helpers/dashboardFiltersQuery';

const KEY_MAP = {
  ...BASE_FILTERS_QUERY_KEYS,
  paymentStatus: 'payment_status',
  agentGroup: 'agent_group',
} as const;

// The toggle never emits an empty set, so an absent query param means this default view.
export const DEFAULT_PAYMENT_STATUS: DealPaymentStatusEnum[] = [
  DealPaymentStatusEnum.paid,
  DealPaymentStatusEnum.due,
];

// A saved set opens whole: every payment status, which the toggle shows as its booked view.
export const SET_PAYMENT_STATUS: DealPaymentStatusEnum[] = [DealPaymentStatusEnum.booked];

function toPaymentStatuses (value: IQueryRecord[string], fallback: DealPaymentStatusEnum[]): DealPaymentStatusEnum[] {
  const valid = Object.values(DealPaymentStatusEnum) as string[];
  const parsed = toArray(value).filter(item => valid.includes(item)) as DealPaymentStatusEnum[];
  return parsed.length ? parsed : [...fallback];
}

export function filtersToQuery (filters: IDealsDashboardFilters): Record<string, string> {
  const out = baseFiltersToQuery(filters);

  if (filters.paymentStatus.length) out[KEY_MAP.paymentStatus] = filters.paymentStatus.join(',');
  if (filters.agentGroup.length) out[KEY_MAP.agentGroup] = filters.agentGroup.join(',');

  return out;
}

export function queryToFilters (query: IQueryRecord): IDealsDashboardFilters {
  const filters = baseQueryToFilters(query);

  return {
    ...filters,
    // A saved set opens on all years and every payment status, so only the URL narrows it.
    year: filters.setId ? toNumberArray(query[KEY_MAP.year]) : filters.year,
    paymentStatus: toPaymentStatuses(query[KEY_MAP.paymentStatus], filters.setId ? SET_PAYMENT_STATUS : DEFAULT_PAYMENT_STATUS),
    agentGroup: toArray(query[KEY_MAP.agentGroup]),
  };
}

export function queryHasFilters (query: IQueryRecord): boolean {
  return baseQueryHasFilters(query, [KEY_MAP.paymentStatus, KEY_MAP.agentGroup]);
}

export function filtersEqual (a: IDealsDashboardFilters, b: IDealsDashboardFilters): boolean {
  return (
    baseFiltersEqual(a, b) &&
    stringArraysEqual(a.paymentStatus, b.paymentStatus) &&
    stringArraysEqual(a.agentGroup, b.agentGroup)
  );
}

export function filtersToDto (filters: IDealsDashboardFilters): IDealsDashboardFiltersDto {
  const dto: IDealsDashboardFiltersDto = dealsPipelineFiltersToDto(filters);

  // `booked` is client-only — strip it. Booked-only leaves payment_status unset,
  // which the API reads as "all statuses" (the intended booked view).
  const apiStatuses = filters.paymentStatus.filter(s => s !== DealPaymentStatusEnum.booked);
  if (apiStatuses.length) dto.payment_status = apiStatuses;

  return dto;
}

export function measureToQuery (measure: DealsMeasureEnum): Record<string, string> {
  return enumToQuery(measure, 'measure', DealsMeasureEnum.consideration);
}

export function queryToMeasure (query: IQueryRecord): DealsMeasureEnum {
  return queryToEnum(query, 'measure', Object.values(DealsMeasureEnum), DealsMeasureEnum.consideration);
}
